import { z } from 'zod';
import { uuidSchema } from './common';
import { PHONE_FIELDS, PHONE_OTHER_RAW_MAX_LENGTH, type PhoneRawField } from '../constants/phone-columns';

/**
 * Чёрный список.
 *
 * Отдельная база, а не статус кандидата: человек попадает в неё до того,
 * как на него заводят заявку, и остаётся после того, как карточку удалят.
 * Подробности — в packages/db/src/schema/blacklist.ts.
 */

export const blacklistEntrySchema = z.object({
  id: uuidSchema,
  fullName: z.string(),
  citizenship: z.string().nullable(),
  phoneByRaw: z.string().nullable(),
  phoneRuRaw: z.string().nullable(),
  phoneExtraRaw: z.string().nullable(),
  reason: z.string().nullable(),
  candidateId: uuidSchema.nullable(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  removedAt: z.string().nullable(),
  removedByName: z.string().nullable(),
  removedReason: z.string().nullable(),
});
export type BlacklistEntry = z.infer<typeof blacklistEntrySchema>;

export const blacklistCreateSchema = z.object({
  fullName: z.string().trim().min(3, 'Укажите ФИО').max(255),
  citizenship: z.string().trim().max(8).optional(),
  phoneByRaw: z.string().trim().max(512).optional(),
  phoneRuRaw: z.string().trim().max(512).optional(),
  phoneExtraRaw: z.string().trim().max(PHONE_OTHER_RAW_MAX_LENGTH).optional(),
  /** Зачем внесли. Не обязательно, но без причины список быстро протухает */
  reason: z.string().trim().max(2000).optional(),
  /** Из чьей карточки внесли — если вносили из карточки кандидата */
  candidateId: uuidSchema.optional(),
});
export type BlacklistCreate = z.infer<typeof blacklistCreateSchema>;

const blacklistUpdateFieldsSchema = blacklistCreateSchema
  .omit({ candidateId: true })
  .partial();
export const blacklistUpdateSchema = blacklistUpdateFieldsSchema.extend({
  /** Values shown when editing started, for protection against concurrent changes. */
  expected: blacklistUpdateFieldsSchema.optional(),
});
export type BlacklistUpdate = z.infer<typeof blacklistUpdateSchema>;

export const blacklistRemoveSchema = z.object({
  reason: z.string().trim().max(2000).optional(),
});

/**
 * По чему список разбивается на группы.
 *
 * Причина сюда не попадает намеренно: это свободный текст («Сбежал»,
 * «эпилепсия», «3 смены, уехала»), и на восьмистах записях он даёт
 * восемьсот групп по одной строке.
 */
export const BLACKLIST_GROUP_FIELDS = [
  { key: 'nameLetter', label: 'алфавиту' },
  { key: 'phone', label: 'номеру телефона' },
  { key: 'status', label: 'статусу' },
  { key: 'citizenship', label: 'гражданству' },
  { key: 'createdBy', label: 'тому, кто внёс' },
  { key: 'createdDate', label: 'дате внесения' },
] as const;
export type BlacklistGroupField =
  (typeof BLACKLIST_GROUP_FIELDS)[number]['key'];
export const BLACKLIST_GROUP_KEYS = BLACKLIST_GROUP_FIELDS.map(
  (f) => f.key,
) as [BlacklistGroupField, ...BlacklistGroupField[]];

export const blacklistListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  /** По умолчанию показываются только действующие записи */
  includeRemoved: z
    .preprocess((value) => value === 'true' || value === true, z.boolean())
    .default(false),
  groupBy: z.enum(BLACKLIST_GROUP_KEYS).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type BlacklistListQuery = z.infer<typeof blacklistListQuerySchema>;

/**
 * Группа в ответе списка.
 *
 * `count` считается тем же условием, что и выборка: иначе заголовок группы
 * покажет число со страницы, а не из группы — «12 записей» там, где их сорок.
 */
export interface BlacklistGroup {
  key: string;
  label: string;
  count: number;
}

/**
 * Строка списка. `groupKey` приходит с сервера тем же выражением, которым
 * считаются порядок и число в группе: считать ключ второй раз на клиенте
 * значило бы завести второй источник правды о том, где кончается группа.
 */
export type BlacklistListItem = BlacklistEntry & { groupKey?: string };

export interface BlacklistListResult {
  items: BlacklistListItem[];
  total: number;
  groups: BlacklistGroup[];
}

/**
 * Запрос сверки: что известно о человеке в момент заведения заявки.
 * Обычно это имя и один телефон — больше на этом шаге и нет.
 */
const blacklistPhoneValue = z.string().trim().max(512).optional();
const blacklistPhoneShape = Object.fromEntries(
  PHONE_FIELDS.map(({ rawField, country }) => [
    rawField,
    country === 'OTHER' ? z.string().trim().max(PHONE_OTHER_RAW_MAX_LENGTH).optional() : blacklistPhoneValue,
  ]),
) as Record<PhoneRawField, typeof blacklistPhoneValue>;

export const blacklistCheckSchema = z.object({
  fullName: z.string().trim().max(255).optional(),
  ...blacklistPhoneShape,
});
export type BlacklistCheck = z.infer<typeof blacklistCheckSchema>;

/** Чем именно совпало — это и показывается человеку. */
export const BLACKLIST_MATCH_REASONS = ['phone', 'name'] as const;
export type BlacklistMatchReason = (typeof BLACKLIST_MATCH_REASONS)[number];

export const blacklistMatchSchema = z.object({
  id: uuidSchema,
  fullName: z.string(),
  reason: z.string().nullable(),
  phoneByRaw: z.string().nullable(),
  phoneRuRaw: z.string().nullable(),
  matchedBy: z.enum(BLACKLIST_MATCH_REASONS),
  createdAt: z.string(),
});
export type BlacklistMatch = z.infer<typeof blacklistMatchSchema>;
